import { InputField } from '@/components/form/InputField';
import { Icons } from '@/components/icons';
import { Loadable } from '@/components/Loadable';
import PriceDisplay from '@/components/PriceDisplay';
import { Button } from '@/components/primitives/Button';
import Divider from '@/components/primitives/Divider';
import { Form } from '@/components/primitives/Form';
import { Typography } from '@/components/primitives/Typography';
import { toastSuccess } from '@/components/toast';
import { getPurchaseFlowBookingById } from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import {
  addVoucherToBooking,
  removeVoucherFromBooking,
  validateVoucher,
} from '@/features/purchase/purchaseService';
import useConfiguredCurrency from '@/hooks/useConfiguredCurrency';
import { useTranslation } from '@/i18n';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type {
  MarkVoucherForPaymentPayloadDTO,
  PendingVoucherDTO,
} from '@/types/dto/agent';
import { createVouchersSchema, type VouchersValues } from '@/utils/zodSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

interface VouchersProps {}

const Vouchers: FC<VouchersProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { currency } = useConfiguredCurrency();
  const booking = useSelector(purchaseFlowBookingSelector);
  const form = useForm<VouchersValues>({
    resolver: zodResolver(createVouchersSchema(t)),
    defaultValues: {
      voucherCode: '',
    },
  });

  const onSubmit = useCallback(
    async (values: VouchersValues) => {
      setLoading(true);
      const { voucherCode } = values;
      if (!booking?.id) return;

      try {
        const { voucherRemainingValue } = await validateVoucher(
          voucherCode,
          booking.id
        );

        const paidAmount: MarkVoucherForPaymentPayloadDTO['paidAmount'] = {
          amount: Math.min(
            voucherRemainingValue,
            booking?.provisionalPrice?.amount || 0
          ),
          currency,
        };

        await addVoucherToBooking(voucherCode, booking.id, paidAmount);
        toastSuccess(<TransAlert i18nKey="voucherApplied" />);
      } catch (e) {
        form.setError('voucherCode', {
          message: t('text.voucherCodeNotValid'),
        });
        return;
      } finally {
        setLoading(false);
      }
      await dispatch(getPurchaseFlowBookingById(booking.id)).unwrap();
    },
    [
      booking?.id,
      booking?.provisionalPrice?.amount,
      currency,
      dispatch,
      form,
      t,
    ]
  );

  const handleRemoveVoucher = useCallback(
    async (voucherCode: string) => {
      setLoading(true);
      if (!booking?.id) return;

      await removeVoucherFromBooking(voucherCode, booking.id);
      setLoading(false);

      await dispatch(getPurchaseFlowBookingById(booking.id)).unwrap();
    },
    [booking?.id, dispatch]
  );

  const renderAppliedVoucher = useCallback(
    (voucher: PendingVoucherDTO, index: number) => (
      <div
        className="align-center flex w-full justify-between rounded-md border border-success-border bg-success-surface py-2 pl-2 pr-3"
        key={index}
      >
        <div className="flex shrink-0 items-center gap-2">
          <Icons.checkCircle className="h-4 w-4 text-success-text" />
          <Typography variant="body2-bold">
            <PriceDisplay
              amount={voucher.amount.amount / 10 ** (voucher.amount.scale || 0)}
            />
          </Typography>
        </div>
        <div className="flex items-center gap-2">
          <Typography className="text-neutral" variant="body2">
            {voucher.voucher_code}
          </Typography>
          <Button
            variant="icon"
            data-testid="remove-applied-voucher-button"
            onClick={() => handleRemoveVoucher(voucher.voucher_code)}
            className="h-6 w-6 text-error-text"
          >
            <Icons.x className="h-6 w-6" />
          </Button>
        </div>
      </div>
    ),
    [handleRemoveVoucher]
  );

  return (
    <Loadable loading={loading} overlay>
      <div className="flex h-full w-full flex-col gap-3 laptop:flex-row laptop:gap-6">
        <div className="flex-1 pt-2">
          <Form
            form={form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-5"
          >
            <InputField
              label={
                <Typography variant="body1-bold" className="mb-2 text-dark">
                  <TransText i18nKey="addVoucher" />:
                </Typography>
              }
              name="voucherCode"
              placeholder="e.g. 2025ACDF10"
              required={true}
            />

            <Button
              variant="secondary"
              className="flex w-full gap-1.5 laptop:w-fit"
              data-testid="add-voucher-button"
            >
              <Icons.check className="h-5 w-5 laptop:h-4 laptop:w-4" />
              <TransText i18nKey="apply" />
            </Button>
          </Form>
        </div>
        <Divider
          orientation="vertical"
          grow={false}
          className="my-2 h-auto self-stretch laptop:my-0"
        />
        <div className="flex w-full flex-1 flex-col gap-2 py-2">
          <Typography variant="body1-bold" className="pb-1">
            <TransText i18nKey="appliedVouchers" />:
          </Typography>
          <div className="flex w-full flex-col gap-2">
            {booking?.pendingVouchers?.length ? (
              booking.pendingVouchers.map(renderAppliedVoucher)
            ) : (
              <Typography variant="body2" className="text-neutral">
                <TransText i18nKey="noVouchersApplied" />
              </Typography>
            )}
          </div>
        </div>
      </div>
    </Loadable>
  );
};

export default Vouchers;
